import React, { useMemo } from "react";
import ReactECharts from "echarts-for-react";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useDemandChargeData } from "../../hooks/useDemandChargeData";
import { formatSignedCurrency, formatWithCommas } from "../../utils/formatNumber";
import Breadcrumbs from "../../../../components/Microgrid/BreadCrumbsFile";
import { buildSimulationCrumbs } from "../../utils/simulationCrumbs";
import { HOURLY_BUCKET_STEP } from "../../utils/chartOptions";
import "./DemandCharge.css";

export default function DemandChargePage() {
  const { hasDemandCharge, loading, dailyDemand, totalDemandCharge } =
    useDemandChargeData();

  const rawCustomerEmail =
    sessionStorage.getItem("selectedCustomerEmail") || "";
  const customerPrefix = rawCustomerEmail.replace(/[@.]/g, "-") || "";
  const fileName = sessionStorage.getItem("timeSeriesFileName");

  const monthlyData = useMemo(() => {
    if (!dailyDemand.length) return [];
    const byMonth = {};
    for (const row of dailyDemand) {
      const month = row.date.slice(0, 7);
      if (!byMonth[month]) {
        byMonth[month] = { month, totalCharge: 0, maxPeak: 0 };
      }
      byMonth[month].totalCharge += row.demandCharge;
      if (row.peakPowerKw > byMonth[month].maxPeak)
        byMonth[month].maxPeak = row.peakPowerKw;
    }
    return Object.values(byMonth).sort((a, b) =>
      a.month.localeCompare(b.month),
    );
  }, [dailyDemand]);

  const chartOption = useMemo(() => {
    if (!dailyDemand.length) return {};

    const dates = dailyDemand.map((r) => r.date);
    const peakPowers = dailyDemand.map((r) => r.peakPowerKw);

    const monthLabels = monthlyData.map((m) => {
      const [y, mo] = m.month.split("-");
      const name = new Date(y, mo - 1).toLocaleString("default", {
        month: "short",
      });
      return `${name} ${y}`;
    });
    const monthCharges = monthlyData.map((m) => m.totalCharge);

    return {
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "cross" },
      },
      legend: {
        data: ["Peak Grid Demand", "Demand Charge"],
        bottom: 0,
      },
      grid: { left: 70, right: 70, top: 40, bottom: 60 },
      xAxis: [
        {
          type: "category",
          data: dates,
          axisLabel: {
            rotate: 45,
            fontSize: 10,
            formatter: (val) => {
              const parts = val.split("-");
              return `${parts[1]}/${parts[2]}`;
            },
          },
          show: true,
          position: "bottom",
        },
        {
          type: "category",
          data: monthLabels,
          axisLabel: { show: false },
          axisTick: { show: false },
          axisLine: { show: false },
          position: "bottom",
        },
      ],
      yAxis: [
        {
          type: "value",
          name: "Peak Grid Demand (kW)",
          position: "left",
          axisLabel: { formatter: "{value}" },
        },
        {
          type: "value",
          name: "Demand Charge ($)",
          position: "right",
          axisLabel: { formatter: "${value}" },
        },
      ],
      series: [
        {
          name: "Peak Grid Demand",
          type: "bar",
          xAxisIndex: 0,
          yAxisIndex: 0,
          data: peakPowers,
          itemStyle: { color: "rgba(83, 79, 213, 0.6)" },
          barMaxWidth: 8,
        },
        /* One dollar figure per MONTH, so it steps - see HOURLY_BUCKET_STEP in
         * utils/chartOptions.js for the rule. The bucket here is a month rather
         * than an hour, so both halves of that rule were re-derived rather than
         * carried over.
         *
         * Is it constant across its bucket? Yes, and not merely by aggregation:
         * a demand charge is billed once on the month's peak, not accrued
         * through it. In the daily summary the sim writes it on exactly ONE day
         * of each month - 12 non-zero rows in 366 for husky-2040-prod - and the
         * monthly sum above therefore carries that single figure rather than a
         * mean of anything. It is the strongest form of piecewise constant in
         * this data.
         *
         * Does the stamp label the START of its bucket, which is what makes
         * "end" the right mode? Checked the same way the hourly convention was,
         * against an identity the file states itself. The charge is the month's
         * peak at a flat $/kW rate, so the peak says which month's charge it is:
         *
         *     charge[M] == 36.60 $/kW * maxPeak[M]     12 of 12 months
         *     charge[M] == 36.60 $/kW * maxPeak[M-1]    0 of 11
         *     charge[M] == 36.60 $/kW * maxPeak[M+1]    0 of 11
         *
         * The figure stamped 2040-03 is March's, derived from March's peak, so
         * its plateau has to span March and jump on the April boundary - which
         * is "end". "start" would report every month's bill one month early.
         *
         * ── The last bucket, and why it is left short ────────────────────
         * Under "end" December gets no plateau, having no stamp to run to.
         * With twelve buckets that is a twelfth of the chart, so it was
         * measured rather than waved through.
         *
         * It costs nothing against what was here before: the straight line this
         * replaces gave NO month a plateau at all - every value existed only at
         * its own point - so December is exactly as well off as it was, while
         * the other eleven each gain their own interval.
         *
         * Appending one null plus one x category (the fix worked out for the
         * hourly charts) WOULD give December a plateau, and the phantom
         * category cannot show here because this axis already hides its labels,
         * ticks and line. It is not shipped, because on its own it does not
         * achieve the thing it is for. Measured against the grid, with the
         * daily axis underneath as the reference a reader actually sees:
         *
         *   December's real days          91.5% -> 100%  of the plot width
         *   now, 12 categories            87.5% ->  95.8%   (no plateau)
         *   + null, 12 categories         88.5% ->  96.2%   plateau, but still
         *                                                   left of December
         *   + null AND boundaryGap:false  91.7% -> 100.0%   lands on December
         *
         * The null alone narrows every bucket from 8.33% to 7.69% and still
         * draws each month half a band left of its own days, because a category
         * axis defaults to boundaryGap:true and puts samples at band CENTRES.
         * Only the boundaryGap change actually aligns them - and that
         * repositions all twelve months to fix one, which is a bigger visible
         * change than the defect. Left for the product owner rather than
         * decided here; the numbers above are the whole of the decision. */
        {
          name: "Demand Charge",
          type: "line",
          xAxisIndex: 1,
          yAxisIndex: 1,
          data: monthCharges,
          step: HOURLY_BUCKET_STEP,
          lineStyle: { width: 3, color: "#ff7541" },
          itemStyle: { color: "#ff7541" },
          symbol: "circle",
          symbolSize: 8,
        },
      ],
      dataZoom: [
        { type: "slider", xAxisIndex: [0, 1], bottom: 30 },
        { type: "inside", xAxisIndex: [0, 1] },
      ],
    };
  }, [dailyDemand, monthlyData]);

  const maxMonthlyPeak = useMemo(
    () =>
      monthlyData.length
        ? Math.max(...monthlyData.map((m) => m.maxPeak))
        : 0,
    [monthlyData],
  );

  if (loading) {
    return (
      <div className="demand-charge-page">
        <h2 className="page-heading">Demand Charge</h2>
        <SkeletonTheme baseColor="#f8f9fa" highlightColor="#e9ecef">
          <Skeleton height={120} style={{ marginBottom: 20 }} />
          <Skeleton height={400} />
        </SkeletonTheme>
      </div>
    );
  }

  if (!hasDemandCharge || !dailyDemand.length) {
    return (
      <div className="demand-charge-page">
        <h2 className="page-heading">Demand Charge</h2>
        <p>No demand charge data available for this simulation.</p>
      </div>
    );
  }

  return (
    <div className="demand-charge-page">
      <h2 className="page-heading">Demand Charge</h2>

      <Breadcrumbs
        crumbs={buildSimulationCrumbs({
          jobTitle: sessionStorage.getItem("jobTitle"),
          customerFirstName: sessionStorage.getItem("customerFirstName"),
          customerPrefix,
          microgridDesign: sessionStorage.getItem("microgridDesign"),
          fileName,
        })}
      />

      <div className="dc-summary-card shadow">
        <div className="dc-summary-row">
          <div className="dc-summary-item">
            <div className="dc-summary-label">Total Demand Charge</div>
            <div className="dc-summary-value dc-summary-value--primary">
              {formatSignedCurrency(totalDemandCharge)}
            </div>
          </div>
          <div className="dc-summary-item">
            <div className="dc-summary-label">Peak Grid Demand (Max)</div>
            <div className="dc-summary-value">
              {formatWithCommas(maxMonthlyPeak, 1)} kW
            </div>
          </div>
          <div className="dc-summary-item">
            <div className="dc-summary-label">Months</div>
            <div className="dc-summary-value">{monthlyData.length}</div>
          </div>
        </div>
      </div>

      <div className="dc-chart-container shadow">
        <ReactECharts
          option={chartOption}
          style={{ height: 450, width: "100%" }}
          notMerge
        />
      </div>
    </div>
  );
}
